import { apiAction, apiFetch } from 'api/api'
import React, { useEffect, useState } from 'react'
import { numberWithCommas } from 'utils/Utils'
import CustomInputElement from 'components/forms/CustomInputElement'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import {
    Row,
    Col,
    Collapse,
} from 'reactstrap'

import {
    Box,
    Slider,
    Tooltip,
} from '@mui/material'
import Loader from 'components/loader/Loader'
import SectionError from 'components/error/SectionError'

const T = {
    bg: '#F6F5F0',
    card: '#FFFFFF',
    line: '#E5E2D8',
    ink: '#232821',
    mut: '#6E766C',
    acc: '#2E9E7A',
};

const DCFAnalysisContainer = (props) => {
    const { postUrl, url, analysis, dcfKey } = props

    const [guide, setGuide] = useState([]);
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState(50);
    const [result, setResult] = useState({});
    const [data, setData] = useState(analysis);
    const [color, setColor] = useState('#2E9E7A');
    const [hoveredBar, setHoveredBar] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [retryTick, setRetryTick] = useState(0);

    useEffect(() => {
        const getApiResults = async () => {
            setFetchError(null);
            const { data: res, error } = await apiFetch({ url })
            if (error) { setFetchError(error); return; }
            if (res) {
                // Blocker 4: destructure instead of delete to avoid mutating the API response object
                const { guide: guideData, ...restRes } = res;
                setGuide(guideData || [])
                // Blocker 2: pure functional update
                setData(prev => {
                    const next = { ...prev };
                    Object.keys(restRes).forEach(item => {
                        if (next[item]) next[item] = { ...next[item], value: restRes[item] };
                    });
                    if (guideData) {
                        guideData.forEach(item => {
                            if (next[item.input]) {
                                next[item.input] = {
                                    ...next[item.input],
                                    value: (item.conservative - item.aggresive) / 1.6 + item.aggresive,
                                };
                            }
                        });
                    }
                    return next;
                });
            }
        }
        getApiResults()
        // eslint-disable-next-line
    }, [url, retryTick])

    // Auto-calculate on input changes
    useEffect(() => {
        const handler = setTimeout(async () => {
            const notEnterdItems = Object.keys(data).filter((item) => {
                if (!data[item].readOnly && !data[item].ignore && (data[item].value === '' || data[item].value === null || data[item].value === undefined)) {
                    return true;
                }
                return false;
            })

            if (!notEnterdItems.length) {
                let body = {}
                Object.keys(data).map((item) => {
                    body[item] = data[item].value
                    return body
                })
                let res = await apiAction({
                    url: postUrl,
                    method: 'post',
                    data: body
                })
                if (dcfKey) {
                    setResult(res ? res : {})
                }
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [data, postUrl, dcfKey]);

    const toggle = () => {
        setOpen(!open);
    }

    const handleChange = (event, newValue) => {
        setValue(newValue);
        if (newValue > 50) onSlideSetTryValue('aggresive', newValue)
        else if (newValue < 50) onSlideSetTryValue('conservative', newValue)
        else {
            setColor('#2E9E7A')
            // Blocker 2: pure functional update
            setData(prev => {
                const next = { ...prev };
                Object.keys(guide).forEach(item => {
                    if (next[guide[item].input]) {
                        next[guide[item].input] = {
                            ...next[guide[item].input],
                            value: (guide[item].conservative - guide[item].aggresive) / 1.6 + guide[item].aggresive,
                        };
                    }
                });
                return next;
            });
        }
    }

    const onSlideSetTryValue = (v, val) => {
        // Blocker 2: compute values immutably
        const computeValue = (item) => {
            const g = guide[item];
            if (!data[g.input]) return null;
            if (v === 'aggresive') {
                if (val === 60)  return (g.aggresive - g.conservative) / 1.5 + g.conservative;
                if (val === 70)  return (g.aggresive - g.conservative) / 1.4 + g.conservative;
                if (val === 80)  return (g.aggresive - g.conservative) / 1.3 + g.conservative;
                if (val === 90)  return (g.aggresive - g.conservative) / 1.2 + g.conservative;
                if (val === 100) return (g.aggresive - g.conservative) / 1   + g.conservative;
            } else {
                if (val === 40) return (g.conservative - g.aggresive) / 1.5 + g.aggresive;
                if (val === 30) return (g.conservative - g.aggresive) / 1.4 + g.aggresive;
                if (val === 20) return (g.conservative - g.aggresive) / 1.3 + g.aggresive;
                if (val === 10) return (g.conservative - g.aggresive) / 1.2 + g.aggresive;
                if (val === 0)  return (g.conservative - g.aggresive) / 1   + g.aggresive;
            }
            return null;
        };
        if (v === 'aggresive') setColor('#FF9800');
        else setColor('#71D875');
        setData(prev => {
            const next = { ...prev };
            Object.keys(guide).forEach(item => {
                const newVal = computeValue(item);
                if (newVal !== null && next[guide[item].input]) {
                    next[guide[item].input] = { ...next[guide[item].input], value: newVal };
                }
            });
            return next;
        });
    }

    const resetBalanced = () => {
        handleChange(null, 50);
    }

    // PV Chart Data Calculation
    const pvDict = result?.fcf_pv_dict || {};
    const pvValues = Object.values(pvDict).map(v => parseFloat(v?.present_value) || 0);
    const maxPv = pvValues.length > 0 ? Math.max(...pvValues.map(Math.abs), 1) : 1;
    const pvSum = pvValues.reduce((a, b) => a + b, 0);
    const terminalPv = result?.terminal_value || 0; // fallback if terminal PV is available, API might return it separately

    return (
        <div style={{
            background: T.card,
            borderRadius: 16,
            border: `1px solid ${T.line}`,
            padding: '24px',
            marginBottom: 16,
        }}>
            {!guide.length ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 18, fontWeight: 700, color: T.ink }}>
                        Discounted Cash Flow
                    </div>
                    {fetchError ? (
                        <SectionError message={fetchError.message} onRetry={() => setRetryTick(t => t + 1)} />
                    ) : (
                        <Loader />
                    )}
                </div>
            ) : (
                <Row>
                    {/* LEFT COLUMN: Controls */}
                    <Col md={6} style={{ paddingRight: 24, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 18, fontWeight: 700, color: T.ink }}>
                                Discounted Cash Flow
                            </span>
                            <div style={{ marginLeft: 'auto', fontSize: 12, color: T.mut }}>
                                10-yr two-stage · live
                            </div>
                        </div>

                        <div 
                            onClick={toggle} 
                            style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: 6, 
                                marginBottom: 12, 
                                cursor: 'pointer',
                            }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>Guide</span>
                            <InfoOutlinedIcon sx={{ fontSize: 18, color: T.mut }} />
                        </div>

                        <div style={{ fontSize: 13, color: T.mut, marginBottom: 16 }}>
                            Drag the profile or edit any assumption — the valuation recalculates instantly.
                        </div>

                        {/* Guide Collapse */}
                        <Collapse isOpen={open}>
                            <div style={{ background: T.bg, borderRadius: 12, padding: '16px', marginBottom: 16, border: `1px solid ${T.line}` }}>
                                {guide.map((item, index) => (
                                    <div key={index} style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
                                            {item.parameter_name}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <CommonToolTip color={'#71D875'} title={'conservative'} value={item.conservative} />
                                            <CommonToolTip color={'#ff9800'} title={'aggresive'} value={item.aggresive} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Collapse>

                        {/* Slider */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: T.mut }}>Conservative</span>
                            <Box sx={{ flex: 1, padding: '0 8px' }}>
                                <Slider
                                    step={10}
                                    sx={{
                                        color: color,
                                        height: 8,
                                        '& .MuiSlider-thumb': {
                                            width: 16,
                                            height: 16,
                                            backgroundColor: color,
                                        },
                                        '& .MuiSlider-track': {
                                            border: 'none',
                                            backgroundColor: color,
                                        },
                                        '& .MuiSlider-rail': {
                                            backgroundColor: '#333',
                                            opacity: 1,
                                        },
                                    }}
                                    value={value}
                                    onChange={handleChange}
                                />
                            </Box>
                            <span style={{ fontSize: 12, fontWeight: 600, color: T.mut }}>Aggressive</span>
                            <div
                                onClick={resetBalanced}
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: T.ink,
                                    background: T.bg,
                                    padding: '4px 12px',
                                    borderRadius: 16,
                                    cursor: 'pointer',
                                    border: `1px solid ${T.line}`
                                }}
                            >
                                Balanced
                            </div>
                        </div>

                        {/* Inputs Grid */}
                        <Row>
                            {Object.keys(data).filter(item => !data[item].readOnly).map((item, index) => (
                                <Col xs={data[item].col || 6} key={index} style={{ marginBottom: 4 }}>
                                    <CustomInputElement
                                        inputType="number"
                                        formGroupClass="mb-0"
                                        labelMargin="0 0 4px 0"
                                        style={{
                                            ...data[item].style,
                                            fontWeight: 600,
                                            color: '#232821',
                                            background: '#F0EEE6',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '10px 12px',
                                            boxShadow: 'none',
                                            height: 40,
                                        }}
                                        requiredState={data[item].requiredState}
                                        label={<span style={{ color: T.mut, display: 'block' }}>{data[item].lable}</span>}
                                        placeHolder={data[item].placeholder}
                                        value={data[item].value}
                                        onChange={(e) => {
                                            // Blocker 2: pure functional update
                                            setData(prev => ({
                                                ...prev,
                                                [item]: { ...prev[item], requiredState: '', value: e.target.value },
                                            }));
                                        }}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* RIGHT COLUMN: Visualizations */}
                    <Col md={6} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {/* Intrinsic Value Card */}
                        <div style={{
                            background: '#F1F7F4', // slight green tint from screenshot
                            borderRadius: 12,
                            padding: '24px',
                            border: '1px solid #D1E5DB',
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: T.mut, letterSpacing: 0.5, marginBottom: 8 }}>
                                INTRINSIC VALUE / SHARE
                            </div>
                            <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 42, fontWeight: 800, color: T.ink, lineHeight: 1 }}>
                                ₹{result?.dcf_per_share ? numberWithCommas(result.dcf_per_share) : '--'}
                            </div>
                        </div>

                        {/* PV Chart Card */}
                        <div style={{
                            background: T.bg,
                            borderRadius: 12,
                            padding: '24px',
                            border: `1px solid ${T.line}`,
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: T.mut, marginBottom: 16 }}>
                                PV of projected FCF, years 1–10
                            </div>

                            <div style={{
                                display: 'flex',
                                gap: 6,
                                alignItems: 'flex-end',
                                flex: 1,
                                minHeight: 80,
                                marginBottom: 12,
                                position: 'relative',
                            }}>
                                {Object.keys(pvDict).length > 0 ? (
                                    Object.keys(pvDict).map((year, i) => {
                                        const entry = pvDict[year];
                                        const isHovered = hoveredBar === i;
                                        const val = pvValues[i];
                                        const absVal = Math.abs(val);
                                        return (
                                            <div
                                                key={i}
                                                onMouseEnter={() => setHoveredBar(i)}
                                                onMouseLeave={() => setHoveredBar(null)}
                                                style={{
                                                    flex: 1,
                                                    background: isHovered ? T.acc : '#81C7A8',
                                                    borderRadius: 4,
                                                    height: `${Math.max((absVal / maxPv) * 100, 5)}%`,
                                                    minHeight: 10,
                                                    cursor: 'pointer',
                                                    transition: 'background 0.15s',
                                                    position: 'relative',
                                                }}
                                            >
                                                {isHovered && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        bottom: 'calc(100% + 8px)',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        background: T.ink,
                                                        color: '#FFFFFF',
                                                        borderRadius: 8,
                                                        padding: '8px 10px',
                                                        fontSize: 11,
                                                        fontWeight: 500,
                                                        whiteSpace: 'nowrap',
                                                        zIndex: 20,
                                                        lineHeight: 1.6,
                                                        pointerEvents: 'none',
                                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                    }}>
                                                        <div style={{ fontWeight: 700, marginBottom: 4, color: '#81C7A8' }}>Year {year}</div>
                                                        <div>FCF: <strong>₹{numberWithCommas(entry?.fcf)}</strong></div>
                                                        <div>PV: <strong>₹{numberWithCommas(entry?.present_value)}</strong></div>
                                                        {entry?.growth_rate !== undefined && (
                                                            <div>Growth: <strong>{numberWithCommas(entry?.growth_rate)}%</strong></div>
                                                        )}
                                                        {/* Arrow */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            bottom: -5,
                                                            left: '50%',
                                                            transform: 'translateX(-50%)',
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '5px solid transparent',
                                                            borderRight: '5px solid transparent',
                                                            borderTop: `5px solid ${T.ink}`,
                                                        }} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.mut, fontSize: 12 }}>
                                        No projection data available
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 600, color: T.mut }}>
                                <span>Σ PV <strong style={{ color: T.ink }}>₹{numberWithCommas(pvSum)} Cr</strong></span>
                                {terminalPv > 0 && <span>Terminal PV <strong style={{ color: T.ink }}>₹{numberWithCommas(terminalPv)} Cr</strong></span>}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </div>
    )
}

const CommonToolTip = (props) => {
    const { color, title, value } = props
    return (
        <Tooltip title={title} arrow
            componentsProps={{
                tooltip: {
                    sx: {
                        color: '#0d0d0d',
                        fontSize: '11px',
                        fontWeight: '600',
                        bgcolor: '#ebf0ff',
                        textTransform: 'capitalize',
                        '& .MuiTooltip-arrow': { color: '#ebf0ff' },
                    },
                },
            }} >
            <div style={{ padding: '2px 8px', borderRadius: 4, background: '#FFFFFF', border: `1px solid ${color}`, display: 'inline-flex' }}>
                <span style={{ color: color, fontSize: 12, fontWeight: 600 }}>{numberWithCommas(value)}</span>
            </div>
        </Tooltip>
    )
}

export default DCFAnalysisContainer
